import { CustomCheckedState } from '../../../components/input/checkbox';
import { MinimalPraksisstedMedHierarkiDto } from '../../../api/generated/studiested-generated-api';
import { MinimalSted, useSelectedStedContext } from './index';

/**
 * Functions in this file are used to manage the state of agreement checkboxes when adding/editing a practice place
 * agreement. The main reason for the high complexity of the file is the usage of the indeterminate state in
 * checkboxes of MinimalSted that are included in the agreement, but just as middle nodes, as it is an invalid state
 * for MinimalSted to be omfattet/specified hierarchically. Most functions are helping functions for the hook
 * useSetCheckedState (defined at the bottom of the file), which is responsible to verify and propagate indeterminate
 * and checked states (to children and parent nodes), when a checkbox is clicked.
 *
 * Example of a legal MinimalSted checkbox hierarchy:
 * STED AA -> indeterminate
 *   STED AAB -> indeterminate
 *     STED AABA -> checked
 *   STED AAA -> checked
 * */

export type AgreementCheckboxes = { [id: string]: CustomCheckedState };

const flattenUndernode = (undernode: MinimalSted): MinimalSted[] => [
  undernode,
  ...undernode.noder.under.flatMap((node) => flattenUndernode(node)),
];

export const flattenUndernoder = (undernoder: MinimalSted[]): MinimalSted[] =>
  undernoder.flatMap(flattenUndernode);

export const getHasAllCheckedOrIndeterminateCheckboxUndernoder = (
  flattenedUndernoder: MinimalSted[],
  checkboxes: AgreementCheckboxes,
): boolean =>
  flattenedUndernoder.length > 0 &&
  flattenedUndernoder.every(
    (node) =>
      checkboxes[node.id].checked ||
      getHasAllCheckedOrIndeterminateCheckboxUndernoder(
        node.noder.under,
        checkboxes,
      ),
  );

export const getCheckedCheckboxUndernoderIds = (
  flattenedUndernoder: MinimalSted[],
  checkboxes: AgreementCheckboxes,
): string[] =>
  flattenedUndernoder
    .filter((node) => checkboxes[node.id].checked)
    .map((node) => node.id);

const getHasAnyCheckedCheckboxUndernoder = (
  flattenedUndernoder: MinimalSted[],
  checkboxes: AgreementCheckboxes,
): boolean => flattenedUndernoder.some((node) => checkboxes[node.id].checked);

const getHasAnyCheckedOrIndeterminateIdUndernoder = (
  flattenedUndernoder: MinimalSted[],
  checkedIds: string[],
): boolean =>
  flattenedUndernoder.length > 0 &&
  flattenUndernoder(flattenedUndernoder).some(
    (node) =>
      checkedIds.includes(node.id) ||
      getHasAnyCheckedOrIndeterminateIdUndernoder(node.noder.under, checkedIds),
  );

export const anyCheckboxesAreChecked = (
  checkboxes: AgreementCheckboxes,
): boolean => Object.values(checkboxes).some(({ checked }) => checked);

export const getHasCheckedParent = (
  node: MinimalSted,
  checkboxes: AgreementCheckboxes,
  toppnode: MinimalSted | undefined,
): boolean => {
  if (!toppnode || node.id === toppnode.id) return false;

  const { over } = node.noder;

  // instanceof Array, i.e. MinimalStudiestedDto[]
  if (over instanceof Array) {
    if (over.length === 0 || over[0].id === toppnode.id) return false;

    // Checks whether the parent also has a parent
    const overHasParent = over.length > 1;
    const overIsChecked = checkboxes[over[0].id].checked;

    return overHasParent
      ? overIsChecked || getHasCheckedParent(over[1], checkboxes, toppnode)
      : overIsChecked;
  }

  if (!over || over.id === toppnode.id) return false;

  const overHasParent = over.noder.over!!;
  const overIsChecked = checkboxes[over.id].checked;

  return overHasParent
    ? overIsChecked || getHasCheckedParent(over, checkboxes, toppnode)
    : overIsChecked;
};

export const getInitCheckboxes = (
  undernoder: MinimalSted[],
  checkedIds: string[],
): AgreementCheckboxes =>
  undernoder.reduce((acc: AgreementCheckboxes, undernode: MinimalSted) => {
    const innerUndernoder = undernode.noder.under;

    const anyCheckedOrIndeterminateUndernoder =
      getHasAnyCheckedOrIndeterminateIdUndernoder(
        flattenUndernoder(innerUndernoder),
        checkedIds,
      );

    const checked = checkedIds.includes(undernode.id);

    acc[undernode.id] = {
      checked,
      indeterminate: !checked && anyCheckedOrIndeterminateUndernoder,
    };

    return { ...acc, ...getInitCheckboxes(innerUndernoder, checkedIds) };
  }, {});

export const mapCheckedCheckboxesToIds = (
  checkboxes: AgreementCheckboxes,
): string[] =>
  Object.entries(checkboxes).reduce(
    (acc: string[], [id, { checked }]) => (checked ? [...acc, id] : acc),
    [],
  );

const getPropagatedChildrenCheckedState = (
  undernoder: MinimalSted[],
  checkboxes: AgreementCheckboxes,
  {
    checked: parentIsChecked,
    indeterminate: parentIsIndeterminate,
  }: CustomCheckedState,
): AgreementCheckboxes =>
  undernoder.reduce((acc: AgreementCheckboxes, sted: MinimalSted) => {
    const stedIsChecked = checkboxes[sted.id].checked;

    const hasUndernoder = sted.noder.under.length > 0;

    if (hasUndernoder) {
      const updatedState = {
        checked: false,
        indeterminate: !parentIsChecked,
      };

      acc[sted.id] = updatedState;

      acc = {
        ...acc,
        ...getPropagatedChildrenCheckedState(sted.noder.under, acc, {
          checked: parentIsChecked || updatedState.checked,
          indeterminate: updatedState.indeterminate,
        }),
      };
    } else {
      acc[sted.id] = {
        checked: !parentIsChecked && (parentIsIndeterminate || stedIsChecked),
        indeterminate: false,
      };
    }

    return acc;
  }, checkboxes);

const getUpdatedCheckedState = (
  { checked, indeterminate }: CustomCheckedState,
  selfNode: MinimalSted,
  checkboxes: AgreementCheckboxes,
): CustomCheckedState => {
  const {
    noder: { under },
  } = selfNode;

  const hasAnyUndernoder = under.length > 0;

  const updatedChecked = hasAnyUndernoder ? indeterminate : !checked;
  const reset = checked && !updatedChecked;

  let updatedIndeterminate = false;
  if (hasAnyUndernoder) {
    updatedIndeterminate = reset
      ? getHasAnyCheckedCheckboxUndernoder(flattenUndernoder(under), checkboxes)
      : !checked && !indeterminate;
  }

  return {
    checked: updatedChecked,
    indeterminate: updatedIndeterminate,
  };
};

const overnodeInCheckboxHierarchy = (
  overnode: MinimalSted,
  checkboxes: AgreementCheckboxes,
): boolean => Object.keys(checkboxes).includes(overnode.id);

/* This func only checks whether to reset indeterminate states, if no children are checked */
const getPropagatedParentCheckedStateResetMinimalStedUpdate = (
  checkboxes: AgreementCheckboxes,
  directOvernode: MinimalSted,
): AgreementCheckboxes => {
  if (!overnodeInCheckboxHierarchy(directOvernode, checkboxes)) {
    // Happens when directOvernode is toppnode or over
    return checkboxes;
  }

  const flattenedUndernoderOfDirectOvernode = flattenUndernode(directOvernode);

  const { checked, indeterminate } = checkboxes[directOvernode.id];

  return {
    ...checkboxes,
    [directOvernode.id]: {
      checked,
      indeterminate:
        indeterminate &&
        getHasAnyCheckedCheckboxUndernoder(
          flattenedUndernoderOfDirectOvernode,
          checkboxes,
        ),
    },
  };
};

// Used when a checkbox is toggled from unchecked to checked
const getPropagatedParentCheckedStateMinimalStedUpdate = (
  checkboxes: AgreementCheckboxes,
  directOvernode: MinimalSted,
): AgreementCheckboxes => {
  if (!overnodeInCheckboxHierarchy(directOvernode, checkboxes)) {
    // Happens when directOvernode is toppnode or over
    return checkboxes;
  }

  const flattenedUndernoderOfDirectOvernode = flattenUndernode(directOvernode);

  return {
    ...checkboxes,
    [directOvernode.id]: {
      checked: false,
      indeterminate: getHasAnyCheckedCheckboxUndernoder(
        flattenedUndernoderOfDirectOvernode,
        checkboxes,
      ),
    },
  };
};

const CheckboxUpdate = {
  CHECK: 'CHECK',
  UNCHECK: 'UNCHECK',
} as const;

const getPropagatedParentCheckedStateUpdatedCheckboxes = (
  checkboxes: AgreementCheckboxes,
  flattenedToppnodeUndernoder: MinimalSted[],
  directOvernodeId: string,
  updateType: keyof typeof CheckboxUpdate,
): AgreementCheckboxes => {
  const directOvernode = flattenedToppnodeUndernoder.find(
    (node) => node.id === directOvernodeId,
  );

  if (!directOvernode) return checkboxes;

  const updateFunc =
    updateType === CheckboxUpdate.CHECK
      ? getPropagatedParentCheckedStateMinimalStedUpdate
      : getPropagatedParentCheckedStateResetMinimalStedUpdate;

  return updateFunc(checkboxes, directOvernode);
};

const getPropagatedParentCheckedStateStudiested = (
  checkboxes: AgreementCheckboxes,
  over: MinimalSted[],
  flattenedToppnodeUndernoder: MinimalSted[],
  updateType: keyof typeof CheckboxUpdate,
): AgreementCheckboxes => {
  if (over.length === 0) return checkboxes;

  const updatedCheckboxes = getPropagatedParentCheckedStateUpdatedCheckboxes(
    checkboxes,
    flattenedToppnodeUndernoder,
    over[0].id,
    updateType,
  );

  return {
    ...updatedCheckboxes,
    ...getPropagatedParentCheckedStateStudiested(
      updatedCheckboxes,
      over.slice(1),
      flattenedToppnodeUndernoder,
      updateType,
    ),
  };
};

const getPropagatedParentCheckedStatePraksissted = (
  checkboxes: AgreementCheckboxes,
  over: MinimalPraksisstedMedHierarkiDto | undefined,
  flattenedToppnodeUndernoder: MinimalSted[],
  updateType: keyof typeof CheckboxUpdate,
): AgreementCheckboxes => {
  if (!over) return checkboxes;

  const updatedCheckboxes = getPropagatedParentCheckedStateUpdatedCheckboxes(
    checkboxes,
    flattenedToppnodeUndernoder,
    over.id,
    updateType,
  );

  return {
    ...updatedCheckboxes,
    ...getPropagatedParentCheckedStatePraksissted(
      updatedCheckboxes,
      over.noder.over,
      flattenedToppnodeUndernoder,
      updateType,
    ),
  };
};

const getPropagatedParentCheckedState = (
  checkboxes: AgreementCheckboxes,
  selfNode: MinimalSted,
  flattenedToppnodeUndernoder: MinimalSted[],
  updateType: keyof typeof CheckboxUpdate,
): AgreementCheckboxes => {
  const { over } = selfNode.noder;

  if (over instanceof Array) {
    return getPropagatedParentCheckedStateStudiested(
      checkboxes,
      over,
      flattenedToppnodeUndernoder,
      updateType,
    );
  }

  return getPropagatedParentCheckedStatePraksissted(
    checkboxes,
    over,
    flattenedToppnodeUndernoder,
    updateType,
  );
};

// IMPORTANT: Must be used within SelectedStedContext
export const useSetCheckedState = (): ((
  id: string,
  selectAllChecked?: boolean,
) => void) => {
  const { toppnode, checkboxes, setCheckboxes, flattenedToppnodeUndernoder } =
    useSelectedStedContext();

  return (id, selectAllChecked) => {
    if (!toppnode) return checkboxes;

    const selectAll = toppnode.id === id;

    if (selectAll) {
      setCheckboxes(
        getPropagatedChildrenCheckedState(toppnode.noder.under, checkboxes, {
          checked: selectAllChecked!!,
          indeterminate: !selectAllChecked,
        }),
      );

      return;
    }

    const selfState = checkboxes[id];
    const selfNode = flattenedToppnodeUndernoder.find(
      (node) => node.id === id,
    )!;

    const updatedState = getUpdatedCheckedState(
      selfState,
      selfNode,
      checkboxes,
    );
    const checkedStateIsReset = selfState.checked && !updatedState.checked;
    const updatedCheckboxes = {
      ...checkboxes,
      [id]: updatedState,
    };

    if (checkedStateIsReset) {
      setCheckboxes({
        ...updatedCheckboxes,
        ...getPropagatedParentCheckedState(
          updatedCheckboxes,
          selfNode,
          flattenedToppnodeUndernoder,
          CheckboxUpdate.UNCHECK,
        ),
      });
    } else {
      // Updated children must be included in params for parent propagation
      const updatedChildrenCheckboxState = getPropagatedChildrenCheckedState(
        selfNode.noder.under,
        updatedCheckboxes,
        updatedState,
      );

      const updatedChildrenAndParentsCheckboxState =
        getPropagatedParentCheckedState(
          updatedChildrenCheckboxState,
          selfNode,
          flattenedToppnodeUndernoder,
          CheckboxUpdate.CHECK,
        );

      setCheckboxes(updatedChildrenAndParentsCheckboxState);
    }
  };
};
